import { Alert, Button, Col, Row, Select, Space, Spin } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { CompanyProductVersionModel, Feature } from '../../models/product-interface';
import CompanyProductCard from './CompanyProductCard';
import Search from 'antd/lib/input/Search';
import { SubscriptionQueries } from '../../queries/subscription-queries';
import SyncProductModal from '../../components/modals/SyncProductModal';
import DemoFeaturesPage from '../products/DemoFeaturesPage';

const { Option } = Select;

interface ProductsTabProps {
  products: CompanyProductVersionModel[] | undefined;
  demoFeatures: Feature[];
  isLoading: boolean;
  companyId: number;
  companyName: string;
  canUpdate: boolean;
  demoCompany: boolean;
}

export function ProductsTab({ products, demoFeatures, demoCompany, isLoading, companyId, companyName, canUpdate }: ProductsTabProps) {
  const [searchText, setSearchText] = useState<string | null>(null);
  const [productSort, setProductSort] = useState<string>('all');
  const [filteredProducts, setFilteredProducts] = useState<CompanyProductVersionModel[] | null>(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [showDemo,setShowDemo] = useState<boolean>(false);
  const [currentProductId, setCurrentProductId] = useState<number | undefined>();
  const syncProductsMutation = SubscriptionQueries.useUpdateProducts(companyId);

  useEffect(() => {
    var data = products ?? [];
    var hideDemo = false;
    if (productSort !== 'all') {
      data = data.filter((x) => x.product.category === productSort);
      hideDemo = true
    }

    if (!!searchText && searchText.length > 0) {
      let text = searchText.toLowerCase();
      data = data.filter(
        (x) =>
          x.product.name.toLowerCase().includes(text) ||
          x.features.some((f) => f.name.toLowerCase().includes(text))
      );

      if(demoCompany && !"demo company".includes(text) && !demoFeatures?.some((f) => f.name.toLowerCase().includes(text))) {
        hideDemo = true
      }
    }

    if(demoCompany) {
      setShowDemo(!hideDemo)
    } else {
      setShowDemo(false)
    }

    setFilteredProducts(data);
  }, [searchText, productSort, products, demoCompany, demoFeatures]);

  const productCategories = () => {
    if(!!!products || products.length === 0) return []

    let categories = products.map(x => x.product.category)

    return [...new Set(categories)];
  };

  return (
    <>
      <Spin spinning={isLoading || syncProductsMutation.isLoading}>
        <Alert
          type='info'
          message={
            <>
              Products are synced with Salesforce nightly at 3am.
              <Button
                className='primary-text'
                type='text'
                icon={<SyncOutlined />}
                onClick={async () => await syncProductsMutation.mutateAsync()}
              >
                Force Sync Now
              </Button>
            </>
          }
        />
        <br />
        <Row justify='end'>
          <Col>
            <Space>
              <Search
                placeholder='Filter by product or feature name'
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: 400 }}
              />
              <Select
                value={productSort}
                onChange={(value: string) => setProductSort(value)}
                style={{ width: 180 }}
              >
                <Option value='all'>All Products</Option>
                {productCategories().map((x) => (
                  <Option key={x} value={x}>{x}</Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
        <br />
        <Row gutter={[16, 16]}>
          {(filteredProducts ?? products)?.map((d) => (
            <Col span={24} key={d.product.id}>
              <CompanyProductCard
                product={d}
                showSyncModal={(productId) => {setSyncModalVisible(true); setCurrentProductId(productId);}}
                canUpdate={canUpdate}
              />
            </Col>
          ))}
          {showDemo && <Col span={24} key={"demo"}>
              <CompanyProductCard
                product={undefined}
                demoFeatures={demoFeatures}
                canUpdate={false}
              />
            </Col>}
        </Row>
      </Spin>
      <SyncProductModal
        isVisible={syncModalVisible}
        handleClose={() => {setSyncModalVisible(false); setCurrentProductId(undefined);}}
        product={products?.find(p => p.product.id === currentProductId)}
        companyIds={[companyId]}
        companyNames={[companyName]}
        canUpdate={canUpdate}
      />
    </>
  );
}
